import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminService } from '../../services/adminService';
import { chatService } from '../../services/supabaseService';
import { ArrowLeft, Send, Check, X } from 'lucide-react';

const AdminFakeProfileChatsPage = () => {
  const { dummyId } = useParams();
  const navigate = useNavigate();
  const [dummy, setDummy] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selectedConvoId, setSelectedConvoId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!dummyId) return;
    adminService.getUser(dummyId).then((data) => {
      setDummy(data);
      if (data?.is_dummy) {
        adminService.listConversationsForUser(dummyId).then(setConversations);
        adminService.listBookingsForProvider(dummyId).then(setBookings);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [dummyId]);

  useEffect(() => {
    if (!selectedConvoId) {
      setMessages([]);
      return;
    }
    chatService.listMessages(selectedConvoId).then(setMessages);
    const interval = setInterval(() => {
      chatService.listMessages(selectedConvoId).then(setMessages);
    }, 3000);
    return () => clearInterval(interval);
  }, [selectedConvoId]);

  const selectedConvo = conversations.find((c) => c.id === selectedConvoId);
  const otherProfile = selectedConvo?.other_profile;
  const receiverId = selectedConvo?.other_id;

  const handleSend = async () => {
    const text = inputText.trim();
    if (!text || !dummyId || !selectedConvoId || !receiverId) return;
    setSending(true);
    try {
      await adminService.sendMessageAsDummy(selectedConvoId, dummyId, receiverId, text);
      setInputText('');
      chatService.listMessages(selectedConvoId).then(setMessages);
    } catch (err) {
      console.error(err);
    }
    setSending(false);
  };

  const handleBookingStatus = async (bookingId, status) => {
    try {
      await adminService.updateBookingStatus(bookingId, status);
      setBookings((prev) => prev.map((b) => (b.id === bookingId ? { ...b, status } : b)));
    } catch (err) {
      console.error(err);
    }
  };

  if (loading || !dummy) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => navigate('/admin/fake-profiles')}
        className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to fake profiles
      </button>
      <h1 className="font-heading font-bold text-2xl text-slate-900 mb-2">
        Chats as {dummy.display_name || dummy.username}
      </h1>
      <p className="text-slate-600 mb-6">Send messages as this dummy profile. Accept or decline booking requests below.</p>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-4">
          <h2 className="font-semibold text-slate-900">Conversations</h2>
          {conversations.length === 0 ? (
            <p className="text-slate-500 text-sm">No conversations yet.</p>
          ) : (
            <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
              {conversations.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setSelectedConvoId(c.id)}
                  className={`w-full text-left p-3 hover:bg-slate-50 ${selectedConvoId === c.id ? 'bg-primary/5' : ''}`}
                >
                  <p className="font-medium text-slate-900">{c.other_profile?.display_name || c.other_profile?.username || 'User'}</p>
                  <p className="text-xs text-slate-500">{c.last_message_at ? new Date(c.last_message_at).toLocaleString() : ''}</p>
                </button>
              ))}
            </div>
          )}

          <h2 className="font-semibold text-slate-900 mt-6">Bookings (as provider)</h2>
          {bookings.length === 0 ? (
            <p className="text-slate-500 text-sm">No bookings.</p>
          ) : (
            <ul className="space-y-2">
              {bookings.map((b) => (
                <li key={b.id} className="p-3 rounded-lg border border-slate-200 bg-white text-sm">
                  <p>{b.user_profiles?.display_name || b.user_profiles?.username || b.user_id}</p>
                  <p className="text-slate-500">{new Date(b.slot).toLocaleString()} · {b.status}</p>
                  {b.status === 'pending' && (
                    <div className="mt-2 flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleBookingStatus(b.id, 'accepted')}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-600 text-white text-xs"
                      >
                        <Check className="h-3 w-3" /> Accept
                      </button>
                      <button
                        type="button"
                        onClick={() => handleBookingStatus(b.id, 'declined')}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-600 text-white text-xs"
                      >
                        <X className="h-3 w-3" /> Decline
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white flex flex-col min-h-[400px]">
          {!selectedConvoId ? (
            <div className="flex-1 flex items-center justify-center text-slate-500 p-6">
              Select a conversation
            </div>
          ) : (
            <>
              <div className="p-3 border-b border-slate-200">
                <p className="font-medium text-slate-900">With {otherProfile?.display_name || otherProfile?.username || 'User'}</p>
              </div>
              <div className="flex-1 overflow-auto p-4 space-y-2">
                {messages.map((m) => (
                  <div
                    key={m.id}
                    className={`flex ${m.sender_id === dummyId ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${
                        m.sender_id === dummyId ? 'bg-primary text-white' : 'bg-slate-100 text-slate-800'
                      }`}
                    >
                      {m.message_text}
                      <p className="text-xs opacity-80 mt-1">{new Date(m.created_at).toLocaleTimeString()}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-3 border-t border-slate-200 flex gap-2">
                <input
                  type="text"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                  placeholder="Send as dummy..."
                  className="flex-1 px-4 py-2 rounded-xl border border-slate-300 text-slate-800"
                />
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={sending || !inputText.trim()}
                  className="px-4 py-2 rounded-xl bg-primary text-white font-medium disabled:opacity-50 flex items-center gap-1"
                >
                  <Send className="h-4 w-4" /> Send
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminFakeProfileChatsPage;
